import React, { useEffect, useMemo, useRef, useState } from "react";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import { useNavigate, useSearchParams } from "react-router-dom";
import { SortingState } from "@tanstack/react-table";
import { checkboxState, compareListState, geoState, ptypeState, selectedGeoNameState, selectedTabState, selectedYearState } from "../../states";
import { buildColumns, buildRecommendedColumns } from "../../helpers";
import { Button, ButtonTheme, ButtonVariant, Heading, TypographyColor, TypographyType, ResizableContainer, VirtualizedTable, Body, BodyColor, BodyType, TableMenu } from "../../ui-elements";
import { RouteConstants, TABLE_STYLE_CONFIG } from "../../constants";
import { useCompareService } from "../../services/Compare.service";

const POI_TABLE_HEADERS = [
    { accessorKey: 'branchName', header: 'PoI Name', enableSorting: true },
    { accessorKey: 'category', header: 'PoI category', enableSorting: true },
    { accessorKey: 'subcategory', header: 'PoI sub-categories', enableSorting: true },
    { accessorKey: 'pincode', header: 'PIN Code', enableSorting: true },
    { accessorKey: 'subdistrict', header: 'Sub-district name', enableSorting: true },
    { accessorKey: 'numberOfReviews', header: 'Number of reviews', enableSorting: true },
    { accessorKey: 'ratings', header: 'Rating', enableSorting: true },
    { accessorKey: 'oldestReviewDate', header: 'Oldest review date', enableSorting: true },
    { accessorKey: 'mobileNumber', header: 'Mobile number', enableSorting: false },
    { accessorKey: 'googleMapLink', header: 'Google Map Link', enableSorting: false },
];

const MBP_TABLE_HEADERS = [
    { accessorKey: 'branch_name', header: 'Name', enableSorting: true },
    { accessorKey: 'presence_type', header: 'Type', enableSorting: true },
    { accessorKey: 'subdistrict', header: 'Sub-district', enableSorting: true },
    { accessorKey: 'google_map_link', header: 'Google Maps Link', enableSorting: false },
];
interface CollisionTablePanelProps {
    markerCarousel: {
        markers: Array<{ type: 'mbp' | 'poi' | 'region'; data: any }>;
        currentIndex: number;
    } | null;
    collisionRadiusKm: number | null;
    onMarkerSelect: (marker: { type: 'mbp' | 'poi' | 'region'; data: any }) => void;
    onClose: () => void;
    poiTableHeaders: any[];
    mbpTableHeaders: any[];
    recommendedRegionsTableData: { tableHeaderData: any[]; topRegions: any[] };
}

type TabKey = 'regions' | 'poi' | 'mbp';

const COLLISION_TABS: { key: TabKey; label: string }[] = [
    { key: 'regions', label: 'Regions' },
    { key: 'poi', label: 'PoI' },
    { key: 'mbp', label: 'MBP' },
];


const TABLE_CONFIGS: Record<TabKey, { tableConfig: any; primaryKey: string }> = {
    regions: { tableConfig: TABLE_STYLE_CONFIG.RECOMMENDED_REGIONS, primaryKey: 'geoId' },
    poi: { tableConfig: { PRIMARY_KEY: 'branchName', PINNED_ROW_INDICES: [0] }, primaryKey: 'branchId' },
    // PRIMARY_KEY must match an actual column accessorKey so the clickable Heading cell is rendered
    mbp: { tableConfig: { PRIMARY_KEY: 'branch_name', PINNED_ROW_INDICES: [0] }, primaryKey: 'unique_key' },
};

export const CollisionTablePanel: React.FC<CollisionTablePanelProps> = ({ markerCarousel, collisionRadiusKm, onMarkerSelect, onClose, poiTableHeaders, mbpTableHeaders, recommendedRegionsTableData }) => {
    const selectedGeo = useRecoilValue(geoState);
    const selectedTab = useRecoilValue(selectedTabState);
    const selectedYear = useRecoilValue(selectedYearState);
    const isChecked = useRecoilValue(checkboxState);
    const compareList = useRecoilValue(compareListState);
    const pType = useRecoilValue(ptypeState);
    const compareService = useCompareService();
    const [searchParams] = useSearchParams();
    const catchment = searchParams.get('catchment');
    const menuRef = useRef<HTMLDivElement>(null);
    const [regionMenuOpenRow, setRegionMenuOpenRow] = useState<number | null>(null);
    const [mbpMenuOpenRow, setMbpMenuOpenRow] = useState<number | null>(null);
    const [selectedGeoName, setSelectedGeoName] = useRecoilState(selectedGeoNameState);
    const setSelectedGeo = useSetRecoilState(geoState);
    
    const navigate = useNavigate();

    

    const regionTabLabel = useMemo(() => {
        if (catchment) return 'Villages';
        if (isChecked?.catchments) return 'Catchments';
        if (selectedGeo?.sub_district || selectedGeo?.village) return 'Villages';
        if (selectedGeo?.district) return selectedTab === 5 ? 'Pincodes' : 'Subdistricts';
        if (selectedGeo?.state) return 'Districts';
        return 'States';
    }, [selectedGeo, selectedTab, isChecked?.catchments, catchment]);

    const [activeTab, setActiveTab] = useState<TabKey>('poi');
    const [poiSorting, setPoiSorting] = useState<SortingState>([]);
    const [mbpSorting, setMbpSorting] = useState<SortingState>([]);

    // Sync active tab to the currently focused marker (currentIndex).
    // Fires on every navigation (next/prev) because setMarkerCarousel creates a new object.
    useEffect(() => {
        if (!markerCarousel?.markers.length) return;
        const current = markerCarousel.markers[markerCarousel.currentIndex];
        if (current.type === 'region') {
            if (catchment || !selectedGeo?.village) setActiveTab('regions');
        } else {
            setActiveTab(current.type);
        }
    }, [markerCarousel]);



    const regionGeoIds = useMemo(
        () => new Set(markerCarousel?.markers.filter(m => m.type === 'region').map(m => m.data?.geoId) ?? []),
        [markerCarousel]
    );
    const regionData = useMemo(() => {
        const fromTopRegions = recommendedRegionsTableData.topRegions.filter(r => regionGeoIds.has(r.geoId));
        if (fromTopRegions.length > 0) return fromTopRegions;
        // Fallback: use the region marker data directly (e.g. catchment level where getRecommendedRegions is skipped)
        return markerCarousel?.markers.filter(m => m.type === 'region').map(m => m.data) ?? [];
    }, [recommendedRegionsTableData.topRegions, regionGeoIds, markerCarousel]);
    const poiData = useMemo(
        () => markerCarousel?.markers.filter(m => m.type === 'poi').map(m => m.data) ?? [],
        [markerCarousel]
    );
    const mbpData = useMemo(
        () => markerCarousel?.markers.filter(m => m.type === 'mbp').map(m => m.data) ?? [],
        [markerCarousel]
    );

    // Determine current compare list based on table header
    const currCompareList = useMemo(() => {
        const headerLabel = recommendedRegionsTableData?.tableHeaderData?.[1]?.header;
        if (headerLabel !== 'Catchment') {
            if (headerLabel === 'District') {
                return new Set(compareList?.districts?.map((i: any) => i.geoId));
            } else if (headerLabel === 'Pincode') {
                return new Set(compareList?.pincodes?.map((i: any) => i.geoId));
            } else if (headerLabel === 'Village') {
                return new Set(compareList?.villages?.map((i: any) => i.geoId));
            } else {
                return new Set(compareList?.subdistricts?.map((i: any) => i.geoId));
            }
        } else if (pType === 'Branch') {
            return new Set(compareList?.Branch?.map((i: any) => i.geoId));
        } else if (pType === 'Partner') {
            return new Set(compareList?.Partner?.map((i: any) => i.geoId));
        }
        return new Set(compareList?.RelationshipManager?.map((i: any) => i.geoId));
    }, [recommendedRegionsTableData?.tableHeaderData, compareList, pType]);

    // Compare action handler
    const handleCompareAction = async (item: any) => {
        if (item?.type === 'mbp') {
            const category = item?.category?.split(' ').join('');
            const mbpCompareList = new Set(compareList?.[category]?.map((i: any) => i.geoId));
            if (mbpCompareList.size >= 9) return;
        } else {
            if (currCompareList.size >= 9) return;
        }
        const payload = {
            addToCompare: [{
                geoId: item?.type === 'mbp' && item?.catchment_id !== null ? item?.catchment_id : item?.geoId,
                level: item?.type === 'mbp' && item?.catchment_id !== null ? -1 : item.geoLevelNumber,
                ...(item.geoLevel === 'pincode' && { parent_id: Number(item.district) }),
                pType: item?.type === 'mbp' && item?.catchment_id !== null ? item?.category : pType
            }]
        };
        await compareService.postCompreList(payload);
    };

    // Delete from compare handler
    const onClickDelete = async (data: any) => {
        const payload = {
            addToCompare: [],
            removeFromCompare: [{
                geoId: data?.type === 'mbp' && data?.catchment_id !== null ? data?.catchment_id : data?.geoId,
                level: data?.type === 'mbp' && data?.catchment_id !== null ? -1 : data.geoLevelNumber,
                ...(data.geoLevel === 'pincode' && { parent_id: Number(data.district) }),
                pType: data?.type === 'mbp' && data?.catchment_id !== null ? data?.category : pType
            }]
        };
        await compareService.postCompreList(payload);
    };

    const navigateTo = (item: any, routeTo: string) => {
        // Close the panel before navigating to reset marker carousel and prevent stale data
        onClose();

        setSelectedGeoName(item?.geoName || item?.branch_name || item?.company_name);

        if (item?.geoLevel === 'catchment' || item?.navigation?.catchment) {
            searchParams.set('catchment', item?.geoId);
            navigate({
                pathname: routeTo === "/dashboard" ? RouteConstants.dashboard : RouteConstants.catchments,
                search: `?${searchParams.toString()}`,
            });
            return;
        }

        const params = new URLSearchParams();
        const selGeo: Record<string, any> = {};

        ['country', 'state', 'district', 'sub_district', 'village'].forEach(key => {
            if (key === 'sub_district' && !item['sub_district'] && item['pincode']) {
                params.append(key, item['pincode']);
                selGeo[key] = item['pincode'];
            } else if (item[key] !== undefined && item[key] !== null && item[key] !== "") {
                params.append(key, item[key]);
                selGeo[key] = item[key];
            }
        });

        setSelectedGeo(selGeo);

        setTimeout(() => navigate({
            pathname: routeTo,
            search: `?${params.toString()}`,
        }), 0);
    };

    // Navigation handler for MBP catchment - navigates to single catchment view in ECIF
    const navigateToMbpCatchment = (item: any) => {
        const catchmentId = item?.navigation?.catchment ?? item?.catchment_id;
        if (catchmentId == null) return;

        setSelectedGeoName(item?.branch_name || item?.company_name);
        searchParams.set('catchment', catchmentId);
        navigate({
            pathname: RouteConstants.dashboard,
            search: `?${searchParams.toString()}`,
        });
    };

    // Menu columns configuration for regions
    const getRegionMenuCols = (item: any) => {
        const baseCols = [
            { id: 'add_compare', label: 'Add to compare', icon: 'add', onClick: (row: any) => handleCompareAction(row), isDisabled:  currCompareList.size >= 9 },
            { id: 'remove', label: 'Remove', icon: 'delete', onClick: (row: any) => onClickDelete(row) },
            { id: 'explore_more', label: 'Explore More', icon: '', onClick: (row: any) => navigateTo(row, RouteConstants.dashboard) },

        ];

        const itemGeoId = item.geoLevel === 'pincode'
            ? item.pincode
            : item.geoLevel === "village"
                ? item.village || item.geoId
                : item.geoLevel === 'catchment'
                    ? item.geoId
                    : item.sub_district || item.district;

        if (currCompareList.has(itemGeoId)) {
            return baseCols.filter(col => col.id !== 'add_compare');
        }
        return baseCols.filter(col => col.id !== 'remove');
    };

    // Menu columns configuration for MBP
    const getMbpMenuCols = (item: any, currentCompareList: Set<any>, noCatchment: boolean) => {
        const baseCols = [
            {
                id: 'add_compare',
                label: 'Add to compare',
                icon: 'add',
                onClick: (row: any) => {
                    if (currentCompareList.size < 9) handleCompareAction(row);
                },
                isDisabled: noCatchment ? true : currentCompareList.size >= 9
            },
            { id: 'remove', label: 'Remove', icon: 'delete', onClick: (row: any) => onClickDelete(row) },
            ...(!noCatchment ? [{
                id: 'explore_more',
                label: 'Explore More',
                icon: '',
                onClick: (row: any) => navigateToMbpCatchment(row),
            }] : []),
        ];

        const isAlreadyAdded = !noCatchment && currentCompareList.has(item.catchment_id);

        if (isAlreadyAdded) {
            return baseCols.filter(col => col.id !== 'add_compare');
        }
        return baseCols.filter(col => col.id !== 'remove');
    };

    const baseRegionColumns = useMemo(
        () => buildRecommendedColumns(recommendedRegionsTableData.tableHeaderData, { selectedYear, disableSorting: true, disableGrowthDropdown: true }),
        [recommendedRegionsTableData.tableHeaderData, selectedYear]
    );

    const regionColumns = useMemo(() => {
        const columns = [...baseRegionColumns];
        if (columns.length > 0) {
            columns.push({
                id: 'actions',
                accessorKey: 'actions',
                header: '',
                enableSorting: false,
                size: 50,
                cell: (info: any) => {
                    const row = info.row.original;
                    const rowIndex = info.row.index;

                    return (
                        <div className="d-flex align-items-center justify-content-center h-100">
                            <TableMenu
                                row={row}
                                cols={getRegionMenuCols(row)}
                                menuRef={menuRef}
                                onMenuClick={(flag: boolean | undefined) =>
                                    setRegionMenuOpenRow(flag ? null : regionMenuOpenRow === rowIndex ? null : rowIndex)
                                }
                                index={rowIndex}
                                selRow={regionMenuOpenRow}
                                showTooltip={new Map([['add_compare', (currCompareList.size >= 9 && regionMenuOpenRow === rowIndex)]])}
                                tooltipText='You have reached the limit of 9 regions. Remove some to add more.'
                            />
                        </div>
                    );
                }
            });
        }
        return columns;
    }, [baseRegionColumns, regionMenuOpenRow, currCompareList]);

    const poiColumns = useMemo(() => buildColumns(POI_TABLE_HEADERS), []);

    const baseMbpColumns = useMemo(() => buildColumns(MBP_TABLE_HEADERS), []);

    const mbpColumns = useMemo(() => {
        const columns = [...baseMbpColumns];
        if (columns.length > 0) {
            columns.push({
                id: 'actions',
                accessorKey: 'actions',
                header: '',
                enableSorting: false,
                size: 50,
                cell: (info: any) => {
                    const row = info.row.original;
                    const rowIndex = info.row.index;

                    // MBP data from myPresence uses navigation.catchment for catchment_id
                    const catchmentId = row?.navigation?.catchment ?? row?.catchment_id;
                    const noCatchment = catchmentId == null;
                    // Per-row compare list: use this row's presence_type to look up the correct category list
                    const category = row?.presence_type?.split(' ').join('');
                    const currentCompareList = new Set(compareList?.[category]?.map((i: any) => i.geoId));
                    // When no catchment, set catchment_id explicitly to null (not undefined)
                    // so handleCompareAction falls back to row's geoId/geoLevelNumber
                    const mbpAsGeo = { ...row, geoId: catchmentId ?? row?.geoId ?? null, catchment_id: catchmentId ?? null, category: row?.presence_type, type: 'mbp' };
                    const menuCols = getMbpMenuCols(mbpAsGeo, currentCompareList, noCatchment);

                    return (
                        <div className="d-flex align-items-center justify-content-center h-100">
                            <TableMenu
                                row={mbpAsGeo}
                                cols={menuCols}
                                menuRef={menuRef}
                                onMenuClick={(flag: boolean | undefined) =>
                                    setMbpMenuOpenRow(flag ? null : mbpMenuOpenRow === rowIndex ? null : rowIndex)
                                }
                                index={rowIndex}
                                selRow={mbpMenuOpenRow}
                                showTooltip={new Map([['add_compare', ((noCatchment || currentCompareList.size >= 9) && mbpMenuOpenRow === rowIndex)]])}
                                tooltipText={noCatchment ? 'No catchment available to compare.' : 'You have reached the limit of 9 regions. Remove some to add more.'}
                            />
                        </div>
                    );
                }
            });
        }
        return columns;
    }, [baseMbpColumns, mbpMenuOpenRow, compareList]);

    const tabData = { regions: regionData, poi: poiData, mbp: mbpData };
    const tabColumns = { regions: regionColumns, poi: poiColumns, mbp: mbpColumns };
    const tabSorting: Record<TabKey, SortingState | undefined> = { regions: undefined, poi: poiSorting, mbp: mbpSorting };
    const tabSetSorting: Record<TabKey, any> = { regions: undefined, poi: setPoiSorting, mbp: setMbpSorting };

    const { tableConfig, primaryKey } = TABLE_CONFIGS[activeTab];

    // Highlighted row key per tab — tracks the currently focused marker in the carousel
    const currentMarker = markerCarousel ? markerCarousel.markers[markerCarousel.currentIndex] : null;
    const selItemByTab: Record<TabKey, string | number | undefined> = {
        regions: currentMarker?.type === 'region' ? currentMarker.data?.geoId : undefined,
        poi: currentMarker?.type === 'poi' ? currentMarker.data?.branchId : undefined,
        mbp: currentMarker?.type === 'mbp' ? currentMarker.data?.unique_key : undefined,
    };

    return (
        <div className="position-absolute left-0 bottom-0 w-100 z-index-21">
            <ResizableContainer
                defaultHeight={window.innerHeight * 0.40}
                minHeight={10}
                maxHeight={window.innerHeight * 0.75}
                enableResize={{ top: true }}
                className="bg-white w-100 border-top d-flex flex-column overflow-hidden"
            >
                {/* Header */}
                <div className="d-flex flex-row w-100 justify-content-between align-items-center padding-4 h-auto">
                    <div className="d-flex gap-1 align-items-center">
                        <span className="material-symbols-outlined fs-24">
                            target
                        </span>
                        <Heading
                            title={`Targeted within ${selectedGeoName}${isChecked?.adjacency ? ' and around' : ''}`}
                            type={TypographyType.h3}
                            colour={TypographyColor.primary}
                            classname="m-0 p-0"
                        />
                        <Body
                            color={BodyColor.primary}
                            type={BodyType.p3}
                            classname="padding-left-1 m-0 padding-top-1"
                        >
                            {collisionRadiusKm != null ? ` ${(Math.PI * collisionRadiusKm ** 2).toFixed(2)} sq km` : ''}
                        </Body>
                    </div>
                    <Button
                        theme={ButtonTheme.primary}
                        variant={ButtonVariant.transparent}
                        type="button"
                        classname="dashboard-icon-wrapper d-flex justify-content-center align-items-center"
                        onClick={onClose}
                    >
                        <span className="material-symbols-outlined fs-24">cancel</span>
                    </Button>
                </div>

                {/* Tabs */}
                <div className="d-flex align-items-center gap-1 p-0 padding-left-2">
                    {COLLISION_TABS.filter(tab => !(tab.key === 'regions' && !catchment && selectedGeo?.village)).map(tab => {
                        const count = tabData[tab.key].length;
                        const isPoiGeoDisabled = tab.key === 'poi' && !selectedGeo?.sub_district && !catchment;
                        const isDisabled = count === 0 || isPoiGeoDisabled;
                        return (
                            <div
                                key={tab.key}
                                className={`d-flex flex-column justify-content-between align-items-center ${isDisabled ? 'cursor-default' : 'cursor-pointer'}`}
                                onClick={() => !isDisabled && setActiveTab(tab.key)}
                            >
                                <Heading
                                    title={isDisabled ? (tab.key === 'regions' ? regionTabLabel : tab.label) : `${tab.key === 'regions' ? regionTabLabel : tab.label} (${count})`}
                                    type={TypographyType.h5}
                                    colour={isDisabled ? TypographyColor.muted : activeTab === tab.key ? TypographyColor.primary : TypographyColor.secondary}
                                    classname="text-center text-truncate padding-2"
                                />
                                <hr className={`m-0 w-100 ${!isDisabled && activeTab === tab.key ? 'selected-cat' : 'non-selected-cat'}`} />
                            </div>
                        );
                    })}
                </div>

                {/* Table */}
                <div className="h-100">
                    <VirtualizedTable
                        data={tabData[activeTab]}
                        columns={tabColumns[activeTab]}
                        primaryKey={primaryKey}
                        tableConfig={tableConfig}
                        emptyPlaceholder="No data available"
                        headerCellStyle={{ background: '#F8F8F8' }}
                        rowStyle={{ backgroundColor: '#FFFFFF' }}
                        selItem={selItemByTab[activeTab]}
                        // onRowClick receives a TanStack Row object — use .original for the raw data
                        onRowClick={(row: any) =>
                            onMarkerSelect({
                                type: activeTab === 'regions' ? 'region' : activeTab,
                                data: row.original ?? row,
                            })
                        }
                        sorting={tabSorting[activeTab]}
                        onSortingChange={tabSetSorting[activeTab]}
                    />
                </div>
            </ResizableContainer>
        </div>
    );
};
